import { env } from '@/env';
import type { ConsentUserMetadataType } from '@/consts/consentTypes';
import type {
  UserData,
  SolutionType,
  PromoCodeResponse,
  MedicareCoverageData,
  TrackType,
} from '@/types/registration';
import { getAppNameByPhoneType, vendorID, deviceModel } from '@/utils/device';
import axios, { AxiosInstance } from 'axios';

const BASE_URL = env.HELLO_SERVER_URL;
const API_PATH = env.IS_DEV ? '' : 'hs';
const BASE_API_URL = `${BASE_URL}/${API_PATH}/`;
const TOKEN = env.HELLO_SERVER_TOKEN;

const SHIPPING_DETAILS_URL = 'adminapi/1.0/shippings/details';
const PROMO_CODE_URL = 'api/1.0/premium/codes/';
const USER_URL = 'api/1.0/users';
const DOWNLOAD_SMS_URL = 'api/2.0/sms/sendDownloadAppSMS';

export type Locale = 'en' | 'es';

export interface RegistrationMetadata {
  webregUserId: string;
  utmCampaign?: string;
  utmContent?: string;
  utmMedium?: string;
  utmSource?: string;
  selectedTracks?: TrackType[];
}

export interface ConsentMetadata {
  version: number;
  status: 'consent' | 'revoked';
  dateReported: number;
  source: string;
  userMetadataObjectType: ConsentUserMetadataType;
  iteration?: number;
}

export interface ConsentsMetadataRequest {
  consents: ConsentMetadata[];
}

export interface PersonalDetails {
  firstname: string;
  lastname: string;
  ['date_of_birth']: string;
  city: string;
  phonenumber: string;
  state: string;
  streetaddress: string;
  zipcode: string;
}

export interface CmsAccessEnrollment {
  tracks: TrackType[];
}

export interface SignupPayload {
  password: string;
  email?: string;
  phoneNumber: string;
  personalDetails: PersonalDetails;
  eligibilityFields?: MedicareCoverageData | null;
  enableSmsMarketing: boolean;
  registrationMetadata: RegistrationMetadata;
  locale: Locale;
  promoCode: string;
  sampleGroup: string;
  privacyPracticeReviewed?: boolean;
  consentToShareData?: boolean;
  consentsMetadata?: ConsentsMetadataRequest;
  cmsAccessEnrollment?: CmsAccessEnrollment;
}

export interface SignupResponse {
  authToken: string;
  user: {
    email: string;
    analyticsID: string;
    dateCreated: number;
    personalDetails: PersonalDetails;
    accountValidated: boolean;
  };
  promoCode: PromoCodeResponse;
  otpLink?: string;
}

export interface ShippingDetailsPayload {
  firstName: string;
  lastName: string;
  streetaddress: string;
  city: string;
  state: string;
  zipcode: string;
  phonenumber: string;
  cuffSize: string;
  solutions: SolutionType[];
}

export interface ShippingDetailsResponse {
  'has-glucose-solution': string;
  'promo-code': string;
  clientID: string;
  'has-bp-shipping-filled': string;
  'has-weight-device-shipping': string;
  'has-bp-device-type': string;
  'has-glucose-device-shipping': string;
  'has-bp-device': string;
  'has-activity-device': string;
  'has-bp-solution': string;
  'has-glucose-device': string;
  'has-bp-device-shipping': string;
  'company-name': string;
  'has-activity-device-shipping': string;
  privacyPracticeReviewed: string;
  'has-weight-device': string;
  language: string;
  'has-activity-solution': string;
  sampleGroup: string;
  'user-source': string;
  enableSmsMarketing: string;
  consentToShareData: string;
  'enrolment-type': string;
  'has-weight-solution': string;
}

class ApiClient {
  private axiosInstance: AxiosInstance;

  constructor(baseURL: string) {
    this.axiosInstance = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add authorization token to all requests except shipping details
    this.axiosInstance.interceptors.request.use((config) => {
      const modifiedConfig = { ...config };
      if (modifiedConfig.url !== SHIPPING_DETAILS_URL) {
        modifiedConfig.headers.Authorization = `Bearer ${TOKEN}`;
      }
      return modifiedConfig;
    });
  }

  async shippingDetails(
    shippingDetails: ShippingDetailsPayload,
    userData: UserData,
  ): Promise<ShippingDetailsResponse> {
    const { authToken } = userData;
    const response = await this.axiosInstance.post(SHIPPING_DETAILS_URL, shippingDetails, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });
    return response.data;
  }

  async promoCode(code: string, token?: string): Promise<PromoCodeResponse> {
    const queryParams = new URLSearchParams();
    if (token) {
      queryParams.append('token', token);
    }
    const url = `${PROMO_CODE_URL}${code}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await this.axiosInstance.get(url);
    return response.data;
  }

  async signup({
    password,
    email,
    phoneNumber,
    personalDetails,
    eligibilityFields,
    enableSmsMarketing,
    registrationMetadata,
    locale,
    promoCode,
    sampleGroup,
    privacyPracticeReviewed = false,
    consentToShareData = false,
    consentsMetadata,
    cmsAccessEnrollment,
  }: SignupPayload): Promise<SignupResponse> {
    const app = getAppNameByPhoneType();
    const properties = {
      locale,
      promoCode,
      sampleGroup,
      privacyPracticeReviewed,
      consentToShareData,
    };

    const response = await this.axiosInstance.post(USER_URL, {
      password,
      app,
      ...(email && { email }),
      phone: phoneNumber,
      securityAnswer: 'deprecated',
      securityQuestion: 'deprecated',
      vendorID,
      deviceModel,
      personalDetails,
      eligibilityFields,
      enableSmsMarketing,
      properties,
      registrationMetadata,
      ...(consentsMetadata && { consentsMetadata }),
      ...(cmsAccessEnrollment && { cmsAccessEnrollment }),
    });

    return response.data;
  }

  async downloadSMS(phone: string, token: string, otpLink?: string): Promise<string> {
    // If we have a valid OTP link, use it directly
    if (otpLink) {
      try {
        new URL(otpLink); // Validate URL
        const response = await this.axiosInstance.post(
          otpLink,
          {
            action: 'popupSubmitDownloadAppPopup',
            phone,
            token,
            site: 'ACCESS_REGISTRATION',
          },
          { baseURL: '' }, // Use the OTP link as-is
        );
        return response.data;
      } catch (error) {
        // Fall through to regular endpoint if OTP link fails
      }
    }

    // Fallback to regular endpoint
    const response = await this.axiosInstance.post(DOWNLOAD_SMS_URL, {
      action: 'popupSubmitDownloadAppPopup',
      phone,
      token,
      site: 'ACCESS_REGISTRATION',
    });
    return response.data;
  }

  async recordFailedRegistration({
    webregUserId,
    identifier,
    reason,
    promoCode,
  }: {
    webregUserId: string;
    identifier: string;
    reason: 'CAP_REACHED';
    promoCode: string;
  }): Promise<{ status: string }> {
    const response = await this.axiosInstance.post(
      `api/2.0/registration/${webregUserId}/failure`,
      {
        identifier,
        reason,
        promoCode,
      },
    );
    return response.data;
  }
}

export enum HelloServerErrorCodes {
  UserAlreadyExists = 1000,
  EligibilityRecordAttachedToUser = 14022,
}

export const apiClient = new ApiClient(BASE_API_URL);

export const DEFAULT_CUFF_SIZE = 'medium';
